import { isAdminEmail, isPlatformAdmin } from './adminAccess';

export type AdminRole =
  | 'super_admin'
  | 'finance_admin'
  | 'support_admin'
  | 'operations_admin'
  | 'template_admin';

export type AdminPermission =
  | 'dashboard.view'
  | 'users.view'
  | 'users.manage'
  | 'billing.view'
  | 'billing.manage'
  | 'ai.view'
  | 'ai.manage'
  | 'documents.view'
  | 'templates.view'
  | 'templates.manage'
  | 'statutes.view'
  | 'statutes.manage'
  | 'workspaces.view'
  | 'support.view'
  | 'support.manage'
  | 'notifications.view'
  | 'notifications.manage'
  | 'system.view'
  | 'system.manage'
  | 'settings.view'
  | 'settings.manage'
  | 'access.view'
  | 'access.manage';

export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  finance_admin: 'Finance Admin',
  support_admin: 'Support Admin',
  operations_admin: 'Operations Admin',
  template_admin: 'Template Admin',
};

export const ADMIN_ROLE_DESCRIPTIONS: Record<AdminRole, string> = {
  super_admin: 'Full platform control including roles and system settings.',
  finance_admin: 'Subscriptions, payments, revenue, and plan permissions.',
  support_admin: 'Support tickets, reports, and lawyer notifications.',
  operations_admin: 'AI monitoring, documents, system health, and workspaces.',
  template_admin: 'Legal templates, categories, and statutes library.',
};

const ALL_PERMISSIONS = Object.keys({
  'dashboard.view': 1,
  'users.view': 1,
  'users.manage': 1,
  'billing.view': 1,
  'billing.manage': 1,
  'ai.view': 1,
  'ai.manage': 1,
  'documents.view': 1,
  'templates.view': 1,
  'templates.manage': 1,
  'statutes.view': 1,
  'statutes.manage': 1,
  'workspaces.view': 1,
  'support.view': 1,
  'support.manage': 1,
  'notifications.view': 1,
  'notifications.manage': 1,
  'system.view': 1,
  'system.manage': 1,
  'settings.view': 1,
  'settings.manage': 1,
  'access.view': 1,
  'access.manage': 1,
}) as AdminPermission[];

const ROLE_PERMISSIONS: Record<AdminRole, AdminPermission[]> = {
  super_admin: ALL_PERMISSIONS,
  finance_admin: [
    'dashboard.view',
    'users.view',
    'billing.view',
    'billing.manage',
  ],
  support_admin: [
    'dashboard.view',
    'users.view',
    'users.manage',
    'support.view',
    'support.manage',
    'notifications.view',
    'notifications.manage',
  ],
  operations_admin: [
    'dashboard.view',
    'users.view',
    'ai.view',
    'ai.manage',
    'documents.view',
    'workspaces.view',
    'notifications.view',
    'system.view',
    'system.manage',
    'settings.view',
    'settings.manage',
  ],
  template_admin: [
    'dashboard.view',
    'templates.view',
    'templates.manage',
    'statutes.view',
    'statutes.manage',
  ],
};

const ADMIN_ROLES = new Set<string>(Object.keys(ADMIN_ROLE_LABELS));

export function isAdminRole(value: string | null | undefined): value is AdminRole {
  return Boolean(value && ADMIN_ROLES.has(value));
}

export function resolveAdminRole(
  email: string | null | undefined,
  userData?: { role?: string | null; adminRole?: string | null } | null,
): AdminRole | null {
  if (
    !isPlatformAdmin(email, userData?.role, userData?.adminRole)
  ) {
    return null;
  }
  if (isAdminEmail(email)) return 'super_admin';
  const stored = userData?.adminRole;
  if (isAdminRole(stored)) return stored;
  if (userData?.role === 'admin') return 'super_admin';
  return null;
}

export function getPermissionsForRole(role: AdminRole | null): Set<AdminPermission> {
  if (!role) return new Set();
  return new Set(ROLE_PERMISSIONS[role]);
}

export function hasAdminPermission(
  role: AdminRole | null,
  permission: AdminPermission,
): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function canManageAdminAccess(role: AdminRole | null): boolean {
  return hasAdminPermission(role, 'access.manage');
}

/** Longest-prefix match for admin route paths. */
export function getRequiredPermissionForPath(pathname: string): AdminPermission | null {
  const rules: { prefix: string; permission: AdminPermission }[] = [
    { prefix: '/admin/access/roles', permission: 'access.manage' },
    { prefix: '/admin/access/permissions', permission: 'access.view' },
    { prefix: '/admin/access', permission: 'access.view' },
    { prefix: '/admin/users', permission: 'users.view' },
    { prefix: '/admin/billing/tiers', permission: 'billing.manage' },
    { prefix: '/admin/billing/plans', permission: 'billing.manage' },
    { prefix: '/admin/billing', permission: 'billing.view' },
    { prefix: '/admin/ai/alerts', permission: 'ai.manage' },
    { prefix: '/admin/ai', permission: 'ai.view' },
    { prefix: '/admin/documents', permission: 'documents.view' },
    { prefix: '/admin/templates', permission: 'templates.view' },
    { prefix: '/admin/statutes', permission: 'statutes.view' },
    { prefix: '/admin/data', permission: 'workspaces.view' },
    { prefix: '/admin/workspaces', permission: 'workspaces.view' },
    { prefix: '/admin/support', permission: 'support.view' },
    { prefix: '/admin/notifications/channels', permission: 'notifications.manage' },
    { prefix: '/admin/notifications/campaigns', permission: 'notifications.manage' },
    { prefix: '/admin/notifications/platform-banners', permission: 'notifications.manage' },
    { prefix: '/admin/notifications/in-app', permission: 'notifications.manage' },
    { prefix: '/admin/notifications', permission: 'notifications.view' },
    { prefix: '/admin/system/notifications', permission: 'system.manage' },
    { prefix: '/admin/system', permission: 'system.view' },
    { prefix: '/admin/settings', permission: 'settings.view' },
    { prefix: '/admin/help', permission: 'dashboard.view' },
    { prefix: '/admin', permission: 'dashboard.view' },
  ];

  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  for (const rule of rules) {
    if (normalized === rule.prefix || normalized.startsWith(`${rule.prefix}/`)) {
      return rule.permission;
    }
  }
  return 'dashboard.view';
}

export function getPermissionMatrix(): {
  role: AdminRole;
  label: string;
  description: string;
  permissions: AdminPermission[];
}[] {
  return (Object.keys(ADMIN_ROLE_LABELS) as AdminRole[]).map((role) => ({
    role,
    label: ADMIN_ROLE_LABELS[role],
    description: ADMIN_ROLE_DESCRIPTIONS[role],
    permissions: ROLE_PERMISSIONS[role],
  }));
}

export const ADMIN_NAV_PERMISSIONS: Record<string, AdminPermission> = {
  dashboard: 'dashboard.view',
  users: 'users.view',
  billing: 'billing.view',
  ai: 'ai.view',
  documents: 'documents.view',
  templates: 'templates.view',
  statutes: 'statutes.view',
  workspaces: 'workspaces.view',
  support: 'support.view',
  notifications: 'notifications.view',
  system: 'system.view',
  settings: 'settings.view',
  access: 'access.view',
  help: 'dashboard.view',
};
